// Orchestrator for every authored transition sequence.
//
// Mounted in `app/root.tsx`, OUTSIDE RouterProvider, so it survives the route
// swap it exists to hide. Choreographing a sequence inside either page cannot
// work: the origin page unmounts halfway through its own performance.
//
// This file owns everything that must happen exactly once per run, and nothing
// that is specific to any one sequence:
//
//   - the clock (one rAF loop, published to renderers via context)
//   - the route commit, fired behind an opaque cover
//   - phase publication, for RouteLayout and destination pages
//   - the watchdog, skip handling, and teardown guarantees
//   - the dev scrub
//
// The visuals live in the renderers; see `registry.ts`.

import { useEffect, useMemo, useRef } from 'react';
import { createPortal } from 'react-dom';
import { useTransitionStore } from '../../stores/transition';
import { useMotionTier } from '../useMotionTier';
import { ms } from '../../theme/motion';
import { SEQUENCES } from './registry';
import { ClockContext, createSequenceClock } from './clock';

export function TransitionHost() {
  const kind = useTransitionStore((s) => s.kind);
  const phase = useTransitionStore((s) => s.phase);
  const runId = useTransitionStore((s) => s.runId);
  const tier = useMotionTier();

  const entry = kind ? SEQUENCES[kind] : null;

  // Rebuilt per run, not just per tier: this component stays mounted for the
  // life of the app, so a memo keyed only on tier would freeze the score at
  // first mount and ignore the dev speed control.
  const score = useMemo(
    () => (entry ? entry.buildScore(tier) : null),
    [entry, tier, runId],
  );

  // One clock per host, stable across runs. Renderers subscribe through context.
  const { clock, emit } = useMemo(() => createSequenceClock(), []);

  const committed = useRef(false);
  const rafId = useRef(0);

  useEffect(() => {
    if (runId === 0 || !score) return;
    if (useTransitionStore.getState().phase === 'idle') return;

    const store = useTransitionStore.getState();
    const startedAt = performance.now();
    committed.current = false;

    // Audio and haptics are garnish. A failure in either — a suspended
    // AudioContext, a vibrate() rejection, an unsupported node on some mobile
    // browser — must never take the visual sequence down with it, which is
    // exactly what an unguarded throw here would do: it aborts before the frame
    // loop is scheduled and the sequence freezes on its first frame.
    try {
      entry?.onStart?.();
    } catch {
      /* non-fatal */
    }

    let mark = 0;

    function commitNow() {
      if (committed.current) return;
      committed.current = true;
      useTransitionStore.getState().commit?.();
    }

    function frame(now: number) {
      const elapsed = (now - startedAt) / 1000;

      // Beat callbacks run off the same clock as the visuals, so audio,
      // navigation and imagery can never drift apart.
      if (mark === 0 && elapsed >= score!.commitAt) {
        mark = 1;
        commitNow();
        useTransitionStore.getState().setPhase('covered');
        try {
          entry?.onCommit?.();
        } catch {
          /* non-fatal */
        }
      }
      if (mark === 1 && elapsed >= score!.settle.at) {
        mark = 2;
        useTransitionStore.getState().setPhase('settling');
      }

      emit(elapsed);

      if (elapsed >= score!.total) {
        useTransitionStore.getState().end();
        return;
      }
      rafId.current = requestAnimationFrame(frame);
    }

    rafId.current = requestAnimationFrame(frame);

    // Watchdog. requestAnimationFrame stops firing entirely when the page is
    // not being rendered — a backgrounded or occluded window. If that happens
    // mid-sequence the frame loop simply stalls, and because the navigation is
    // committed from inside that loop the player would come back to a frozen
    // cover and never reach the destination. setTimeout keeps running in that
    // state, so it is the right belt to the rAF braces.
    const watchdog = window.setTimeout(
      () => {
        if (useTransitionStore.getState().phase === 'idle') return;
        cancelAnimationFrame(rafId.current);
        commitNow();
        useTransitionStore.getState().end();
      },
      ms(score.total) + 500,
    );

    // Dev affordance: scrub the sequence to an arbitrary time.
    //
    //     window.__seqScrub(0.62)   // paint the frame at t = 0.62s
    //
    // Exact rather than approximate, because a sequence is a pure function of
    // elapsed time. The only way to inspect frame-by-frame in an environment
    // where rAF is throttled (headless or occluded windows).
    if ((import.meta as unknown as { env?: { DEV?: boolean } }).env?.DEV) {
      const w = window as unknown as {
        __seqScrub?: (s: number) => void;
        __tapeScrub?: (s: number) => void;
      };
      w.__seqScrub = emit;
      w.__tapeScrub = emit; // original name, referenced in the audit docs
    }

    return () => {
      cancelAnimationFrame(rafId.current);
      window.clearTimeout(watchdog);
      // Never leave the app stranded on the origin route because the sequence
      // was torn down mid-flight.
      if (!committed.current) {
        committed.current = true;
        store.commit?.();
      }
    };
  }, [runId, score, entry, emit]);

  // Skip. A player who has seen a sequence ten times must be able to cut it
  // short — but skipping still has to commit the navigation.
  useEffect(() => {
    if (phase === 'idle') return;
    function finishNow() {
      cancelAnimationFrame(rafId.current);
      const s = useTransitionStore.getState();
      if (!committed.current) {
        committed.current = true;
        s.commit?.();
      }
      s.end();
    }
    window.addEventListener('pointerdown', finishNow);
    window.addEventListener('keydown', finishNow);
    return () => {
      window.removeEventListener('pointerdown', finishNow);
      window.removeEventListener('keydown', finishNow);
    };
  }, [phase]);

  if (phase === 'idle' || !entry || !score) return null;

  const Renderer = entry.Renderer;

  return createPortal(
    <ClockContext.Provider value={clock}>
      <div
        aria-hidden
        className="pointer-events-auto fixed inset-0 z-[120] overflow-hidden"
      >
        <Renderer score={score} />
      </div>
    </ClockContext.Provider>,
    document.body,
  );
}
